/**
 * BurningLeadsButton — pill button + drawer that surfaces
 * `/api/ai-copilot/burning-leads` so agents can jump straight to
 * customers needing urgent follow-up.
 *
 * Read-only: clicking a row opens the existing inbox conversation (if
 * any) or focuses the abandoned checkout context. No state mutation.
 *
 * The button hides itself if the tenant doesn't have AI Copilot OR
 * burning leads enabled. It polls every 60s while mounted so the
 * count stays roughly fresh without slamming the API.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Flame, X, Loader2, MessageCircle, ShoppingCart, ExternalLink, Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function relative(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ''; }
}

export default function BurningLeadsButton({ onSelectConversation }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hiddenForTenant, setHiddenForTenant] = useState(false);
  const pollRef = useRef(null);

  const fetchBurning = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/ai-copilot/burning-leads?limit=50`, {
        headers: authHeaders(),
      });
      if (res.data?.reason === 'disabled') {
        setHiddenForTenant(true);
        return;
      }
      setHiddenForTenant(false);
      setItems(res.data?.burning_leads || []);
      setCount(res.data?.count || 0);
    } catch (e) {
      // 403 / 404 — assume not available for this tenant
      setHiddenForTenant(true);
    }
  }, []);

  useEffect(() => {
    fetchBurning();
    pollRef.current = setInterval(fetchBurning, 60000);
    return () => clearInterval(pollRef.current);
  }, [fetchBurning]);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    await fetchBurning();
    setLoading(false);
  };

  if (hiddenForTenant) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
          count > 0
            ? 'bg-red-500/15 text-red-300 border-red-500/40 hover:bg-red-500/25'
            : 'bg-slate-700/40 text-slate-300 border-slate-600 hover:bg-slate-700/60'
        }`}
        data-testid="burning-leads-button"
        title="Customers who need urgent follow-up"
      >
        <Flame className={`w-3.5 h-3.5 ${count > 0 ? 'animate-pulse' : ''}`} />
        Burning
        {count > 0 && (
          <span
            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold px-1"
            data-testid="burning-leads-count"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-end" onClick={() => setOpen(false)}>
          <div
            className="bg-wt-surface w-full max-w-xl h-full overflow-y-auto border-l border-wt-border"
            onClick={(e) => e.stopPropagation()}
            data-testid="burning-leads-drawer"
          >
            <div className="sticky top-0 bg-wt-surface/95 backdrop-blur px-5 py-3 border-b border-wt-border flex items-center justify-between z-10">
              <h3 className="text-sm font-semibold text-wt-text-primary flex items-center gap-2">
                <Flame className="w-4 h-4 text-red-400" /> Burning Leads
                <Badge className="bg-red-500/20 text-red-300 border-red-500/40 text-[10px]">{count}</Badge>
              </h3>
              <button onClick={() => setOpen(false)} className="text-wt-text-muted hover:text-wt-text-primary" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {loading ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-wt-gold" /></div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-wt-text-muted text-sm">
                <Flame className="w-10 h-10 mx-auto mb-2 opacity-30" />
                Nothing burning right now — great work!
              </div>
            ) : (
              <div className="divide-y divide-wt-border">
                {items.map((item, idx) => {
                  const isCheckout = item.type === 'abandoned_checkout';
                  const Icon = isCheckout ? ShoppingCart : MessageCircle;
                  return (
                    <div
                      key={item.checkout_id || item.conversation_id || idx}
                      className="px-5 py-3 hover:bg-wt-paper/40 transition-colors"
                      data-testid={`burning-row-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-md flex-shrink-0 ${
                          isCheckout ? 'bg-rose-500/10 text-rose-400' : 'bg-amber-500/10 text-amber-400'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-wt-text-primary truncate">
                              {item.customer_name || item.customer_phone || item.customer_email || 'Unknown'}
                            </span>
                            <Badge className="bg-red-500/10 text-red-300 border-red-500/30 text-[10px]">
                              Urgency {item.urgency_score}
                            </Badge>
                          </div>
                          <p className="text-xs text-wt-text-muted mt-0.5">{item.reason_text}</p>
                          {item.last_message && (
                            <p className="text-xs text-wt-text-secondary mt-1 line-clamp-2">"{item.last_message}"</p>
                          )}
                          {item.detected_interest && (
                            <p className="text-xs text-wt-text-secondary mt-1">
                              Interested in: <span className="font-semibold">{item.detected_interest}</span>
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-[11px] text-wt-text-muted">
                            {item.cart_value && (
                              <span>Cart {item.currency || ''} {item.cart_value}</span>
                            )}
                            {item.last_interaction_at && (
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {relative(item.last_interaction_at)}</span>
                            )}
                            {item.has_open_task && <span className="text-amber-400">Task open</span>}
                          </div>
                          <div className="flex gap-2 mt-2">
                            {item.conversation_id && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  onSelectConversation?.(item.conversation_id);
                                  setOpen(false);
                                }}
                                className="bg-wt-gold text-black hover:bg-wt-gold/90 text-xs h-7"
                                data-testid={`burning-open-chat-${idx}`}
                              >
                                <MessageCircle className="w-3 h-3 mr-1" /> Open chat
                              </Button>
                            )}
                            {item.checkout_url && (
                              <a
                                href={item.checkout_url}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-rose-300 hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" /> Open cart
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
